import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { PosthogService } from './posthog.service';
import { ProductAnalyticsContext } from './product-analytics';

@Injectable({ providedIn: 'root' })
export class RouteAnalyticsService {
  private readonly posthog = inject(PosthogService);
  private readonly router = inject(Router);

  initialize(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => this.trackCurrentRoute());
  }

  private trackCurrentRoute(): void {
    const context = buildProductRouteContext(
      this.router.routerState.snapshot.root,
      this.router.url
    );
    this.posthog.setProductContext(context);
    this.posthog.captureProductEvent('area viewed', {
      navigation_type: 'route',
    });

    if (context.feature === 'configurable_dashboards') {
      this.posthog.captureProductEvent('dashboard viewed', {
        dashboard_key: context.entity_id,
      });
    } else if (context.feature === 'load_boards') {
      this.posthog.captureProductEvent('load board viewed', {
        board_id: context.entity_id,
      });
    }
  }
}

export function buildProductRouteContext(
  root: ActivatedRouteSnapshot,
  routerUrl: string
): ProductAnalyticsContext {
  const snapshots = flattenRouteSnapshots(root);
  const routeTemplate = `/${snapshots
    .map((snapshot) => snapshot.routeConfig?.path ?? '')
    .filter((path) => path && path !== '**')
    .join('/')}`.replace(/\/{2,}/g, '/');
  const rawPathname = routerUrl.split(/[?#]/, 1)[0] || '/';
  const params = Object.assign(
    {},
    ...snapshots.map((snapshot) => snapshot.params)
  );
  const classification = classifyRoute(routeTemplate || rawPathname, params);

  return {
    ...classification,
    raw_pathname: rawPathname,
    route_template: routeTemplate || '/',
  };
}

export function classifyRoute(
  routeTemplate: string,
  params: Record<string, unknown> = {}
): Pick<
  ProductAnalyticsContext,
  'area' | 'entity_id' | 'entity_type' | 'feature'
> {
  const segments = routeTemplate
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.toLowerCase());
  const area = segments[0] || 'dashboards';
  let feature: string;

  if (area === 'dashboards' || segments.length === 0) {
    feature = 'configurable_dashboards';
  } else if (area === 'shipments') {
    feature = classifyShipmentFeature(segments, params);
  } else if (area === 'smart-inbox') {
    feature = 'smart_inbox';
  } else if (area === 'my-profile') {
    feature = 'user_profile';
  } else {
    feature = classifyGenericFeature(area, segments);
  }

  const entity = resolveEntity(area, feature, params);
  return { area, feature, ...entity };
}

function flattenRouteSnapshots(
  root: ActivatedRouteSnapshot
): ActivatedRouteSnapshot[] {
  const snapshots: ActivatedRouteSnapshot[] = [];
  let current: ActivatedRouteSnapshot | null = root;
  while (current) {
    snapshots.push(current);
    current = current.firstChild;
  }
  return snapshots;
}

function classifyShipmentFeature(
  segments: string[],
  params: Record<string, unknown>
): string {
  if (segments.includes('boards')) {
    return 'load_boards';
  }
  if (segments.includes('quotes')) {
    if (segments.includes('quick')) {
      return 'quick_quote';
    }
    if (segments.includes('zip')) {
      return 'zip_quote';
    }
    if (segments.includes('multi')) {
      return 'multi_quote';
    }
    if (segments.includes('custom')) {
      return 'custom_load';
    }
    if (segments.includes('new')) {
      return 'api_quote';
    }
    if (segments.includes('scheduled')) {
      return 'shipment_scheduled';
    }
    return segments.some((segment) => segment.startsWith(':'))
      ? 'quote_detail'
      : 'quote_list';
  }
  if (segments.includes('advanced')) {
    return 'advanced_shipment_list';
  }
  if (segments.includes('list')) {
    return 'shipment_list';
  }
  if (segments.includes('import')) {
    return 'shipment_import';
  }
  if (segments.includes('packages')) {
    return 'packages';
  }
  if (segments.some((segment) => segment === ':loadnumber')) {
    const tab =
      segments.at(-1) === ':tab' && typeof params['tab'] === 'string'
        ? params['tab'].toLowerCase().replace(/[^a-z0-9]+/g, '_')
        : undefined;
    return tab ? `shipment_${tab}` : 'shipment_detail';
  }
  return 'shipments_home';
}

function classifyGenericFeature(area: string, segments: string[]): string {
  const staticSegments = segments
    .slice(1)
    .filter((segment) => !segment.startsWith(':'));
  if (staticSegments.length === 0) {
    return segments.some((segment) => segment.startsWith(':'))
      ? `${area.replaceAll('-', '_')}_detail`
      : area.replaceAll('-', '_');
  }
  return [area, ...staticSegments]
    .join('_')
    .replaceAll('-', '_')
    .replace(/[^a-z0-9_]/g, '');
}

function resolveEntity(
  area: string,
  feature: string,
  params: Record<string, unknown>
): Pick<ProductAnalyticsContext, 'entity_id' | 'entity_type'> {
  const candidates: [string, string][] = [
    ['loadNumber', 'shipment'],
    ['invoiceId', 'invoice'],
    ['claimId', 'claim'],
    ['boardId', 'load_board'],
    ['dashboardKey', 'dashboard'],
    ['customerId', 'customer'],
    ['carrierId', 'carrier'],
    ['locationId', 'location'],
  ];
  for (const [key, type] of candidates) {
    const value = params[key];
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      return { entity_id: `${value}`, entity_type: type };
    }
  }

  const id = params['id'];
  if (id === undefined || id === null || `${id}`.length === 0) {
    return {};
  }
  const entityType = feature.includes('quote')
    ? 'quote'
    : area === 'claims'
      ? 'claim'
      : area === 'locations'
        ? 'location'
        : area === 'carriers'
          ? 'carrier'
          : area.slice(0, -1) || 'record';
  return { entity_id: `${id}`, entity_type: entityType };
}
